import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { FiDatabase, FiRefreshCw, FiTrash2 } from "react-icons/fi"

import { OpenAPI } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import { VisionButton, VisionCard } from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { useState } from "react"

export const Route = createFileRoute("/_layout/admin-database")({
  component: AdminDatabase,
})

function apiFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}/api/v1/admin${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...options?.headers },
  })
}

function AdminDatabase() {
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common"])
  const showToast = useCustomToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [truncateTarget, setTruncateTarget] = useState("")
  const [confirmDeleteSim, setConfirmDeleteSim] = useState(false)

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["admin-db-summary"],
    queryFn: async () => {
      const res = await apiFetch("/database/summary")
      if (!res.ok) throw new Error("Failed to load")
      return res.json()
    },
  })

  const { data: tables = [], isLoading: tablesLoading } = useQuery({
    queryKey: ["admin-db-tables"],
    queryFn: async () => {
      const res = await apiFetch("/database/tables")
      if (!res.ok) throw new Error("Failed to load")
      return res.json()
    },
  })

  const { data: hypertables = [] } = useQuery({
    queryKey: ["admin-db-hypertables"],
    queryFn: async () => {
      const res = await apiFetch("/database/hypertables")
      if (!res.ok) throw new Error("Failed to load")
      return res.json()
    },
  })

  const { data: indexes = [] } = useQuery({
    queryKey: ["admin-db-indexes"],
    queryFn: async () => {
      const res = await apiFetch("/database/indexes")
      if (!res.ok) throw new Error("Failed to load")
      return res.json()
    },
  })

  const truncateMutation = useMutation({
    mutationFn: async (tableName: string) => {
      const res = await apiFetch(`/database/truncate/${tableName}`, {
        method: "POST",
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || "Failed")
      }
      return res.json()
    },
    onSuccess: (data) => {
      showToast.showSuccessToast(data.message)
      queryClient.invalidateQueries({ queryKey: ["admin-db-tables"] })
      queryClient.invalidateQueries({ queryKey: ["admin-db-hypertables"] })
    },
    onError: (err: any) => {
      showToast.showErrorToast(err.message)
    },
  })

  const deleteSimMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch("/database/delete-simulation", {
        method: "POST",
      })
      if (!res.ok) throw new Error("Failed")
      return res.json()
    },
    onSuccess: (data) => {
      showToast.showSuccessToast(
        `Deleted: ${data.deleted.tags} tags, ${data.deleted.readings} readings`,
      )
      queryClient.invalidateQueries({ queryKey: ["admin-db-tables"] })
      queryClient.invalidateQueries({ queryKey: ["admin-db-hypertables"] })
    },
    onError: (err: any) => {
      showToast.showErrorToast(err.message)
    },
  })

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-db-summary"] })
    queryClient.invalidateQueries({ queryKey: ["admin-db-tables"] })
    queryClient.invalidateQueries({ queryKey: ["admin-db-hypertables"] })
    queryClient.invalidateQueries({ queryKey: ["admin-db-indexes"] })
  }

  const isLoading = summaryLoading || tablesLoading

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 3, md: 0 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box minW={0}>
          <Heading size={{ base: "lg", md: "xl" }} color={sc.heading} mb={2}>
            <FiDatabase
              style={{
                display: "inline",
                marginRight: "12px",
                verticalAlign: "middle",
              }}
            />
            {t("admin:database.title")}
          </Heading>
          <Text color={sc.textMuted} fontSize={{ base: "sm", md: "md" }}>
            {t("admin:database.subtitle")}
          </Text>
        </Box>
        <VisionButton variant="ghost" size="sm" onClick={refreshAll}>
          <FiRefreshCw /> {t("common:actions.refresh")}
        </VisionButton>
      </Flex>

      {isLoading ? (
        <Flex justify="center" py={12}>
          <Spinner color={sc.spinnerColor} size="xl" />
        </Flex>
      ) : (
        <VStack gap={6} align="stretch">
          {/* Database Summary */}
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("admin:database.summary")}
            </Heading>
            <Flex gap={{ base: 4, md: 8 }} wrap="wrap">
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                >
                  {t("admin:database.totalSize")}
                </Text>
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  color={sc.heading}
                  fontWeight="bold"
                >
                  {summary?.database_size || "—"}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                >
                  {t("admin:database.tableCount")}
                </Text>
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  color={sc.heading}
                  fontWeight="bold"
                >
                  {tables.length}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                >
                  {t("admin:database.hypertableCount")}
                </Text>
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  color={sc.heading}
                  fontWeight="bold"
                >
                  {hypertables.length}
                </Text>
              </Box>
            </Flex>
          </VisionCard>

          {/* Tables */}
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("admin:database.tables")}
            </Heading>
            <Box overflowX="auto" maxW="100%">
              <Table.Root size="sm" style={{ minWidth: "640px" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:database.tableName")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="right">
                      {t("admin:database.rows")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="right">
                      {t("admin:database.dataSize")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="right">
                      {t("admin:database.indexSize")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="right">
                      {t("admin:database.totalSizeCol")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="right">
                      {t("common:table.actions")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {tables.map((tbl: any) => (
                    <Table.Row key={tbl.name}>
                      <Table.Cell
                        color={sc.heading}
                        fontFamily="mono"
                        fontSize="sm"
                      >
                        {tbl.name}
                        {hypertables.some((h: any) => h.name === tbl.name) && (
                          <Badge ml={2} colorScheme="purple" size="sm">
                            hypertable
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="right"
                        color={sc.textSecondary}
                        fontSize="sm"
                      >
                        {tbl.estimated_rows?.toLocaleString() || "0"}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="right"
                        color={sc.textSecondary}
                        fontSize="sm"
                      >
                        {tbl.data_size}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="right"
                        color={sc.textSecondary}
                        fontSize="sm"
                      >
                        {tbl.index_size}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="right"
                        color={sc.heading}
                        fontWeight="500"
                        fontSize="sm"
                      >
                        {tbl.total_size}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <HStack gap={1} justify="flex-end">
                          <VisionButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate({
                                to: "/admin-table-data",
                                search: { table: tbl.name },
                              })
                            }
                            title={t("admin:database.viewData")}
                          >
                            <FiDatabase />
                          </VisionButton>
                          {["tagreading", "tagevent"].includes(tbl.name) && (
                            <VisionButton
                              variant="ghost"
                              size="sm"
                              colorPalette="red"
                              onClick={() => setTruncateTarget(tbl.name)}
                              title="Truncate"
                            >
                              <FiTrash2 />
                            </VisionButton>
                          )}
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </VisionCard>

          {/* Hypertables */}
          {hypertables.length > 0 && (
            <VisionCard>
              <Heading size="md" color={sc.heading} mb={4}>
                TimescaleDB Hypertables
              </Heading>
              <Box overflowX="auto" maxW="100%">
                <Table.Root size="sm" style={{ minWidth: "480px" }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader color={sc.textMuted}>
                        {t("admin:database.tableName")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader
                        color={sc.textMuted}
                        textAlign="right"
                      >
                        Chunks
                      </Table.ColumnHeader>
                      <Table.ColumnHeader
                        color={sc.textMuted}
                        textAlign="right"
                      >
                        {t("admin:database.totalSizeCol")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {hypertables.map((ht: any) => (
                      <Table.Row key={ht.name}>
                        <Table.Cell
                          color={sc.heading}
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          {ht.name}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          color={sc.textSecondary}
                          fontSize="sm"
                        >
                          {ht.num_chunks}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          color={sc.heading}
                          fontWeight="500"
                          fontSize="sm"
                        >
                          {ht.total_size}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            </VisionCard>
          )}

          {/* Indexes */}
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("admin:database.indexes")}
            </Heading>
            <Box overflowX="auto" maxW="100%">
              <Table.Root size="sm" style={{ minWidth: "640px" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color={sc.textMuted}>
                      Index
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:database.tableName")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="right">
                      {t("admin:database.totalSizeCol")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {indexes.map((idx: any) => (
                    <Table.Row key={idx.name}>
                      <Table.Cell
                        color={sc.heading}
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {idx.name}
                      </Table.Cell>
                      <Table.Cell
                        color={sc.textSecondary}
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {idx.table}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="right"
                        color={sc.textSecondary}
                        fontSize="sm"
                      >
                        {idx.size}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </VisionCard>

          {/* Danger Zone */}
          <VisionCard>
            <Heading size="md" color="red.400" mb={4}>
              {t("admin:database.dangerZone")}
            </Heading>
            <VStack align="stretch" gap={3}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Text color={sc.heading} fontSize="sm" fontWeight="500">
                    {t("admin:database.deleteSimulation")}
                  </Text>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("admin:database.deleteSimulationDesc")}
                  </Text>
                </Box>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  colorPalette="red"
                  onClick={() => setConfirmDeleteSim(true)}
                  loading={deleteSimMutation.isPending}
                >
                  <FiTrash2 /> {t("common:actions.delete")}
                </VisionButton>
              </Flex>
            </VStack>
          </VisionCard>
        </VStack>
      )}

      <ConfirmDialog
        open={!!truncateTarget}
        onClose={() => setTruncateTarget("")}
        onConfirm={() => truncateMutation.mutate(truncateTarget)}
        title={`Truncate ${truncateTarget}`}
        message={t("admin:database.truncateWarning", { table: truncateTarget })}
        loading={truncateMutation.isPending}
      />

      <ConfirmDialog
        open={confirmDeleteSim}
        onClose={() => setConfirmDeleteSim(false)}
        onConfirm={() => deleteSimMutation.mutate()}
        title={t("admin:database.deleteSimulation")}
        message={t("admin:database.deleteSimulationWarning")}
        loading={deleteSimMutation.isPending}
      />
    </Box>
  )
}
